import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Search,
  Filter,
  CheckSquare,
  Square,
  Loader2,
  Download,
  Upload,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Check,
  X,
  Image as ImageIcon,
  Calendar
} from 'lucide-react';

const AdminVillas = () => {
  const [villas, setVillas] = useState([]);
  const [filteredVillas, setFilteredVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVillas, setSelectedVillas] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [syncingVilla, setSyncingVilla] = useState(null);
  const [calendarVillaId, setCalendarVillaId] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [villasPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);

  // Filtres
  const [filters, setFilters] = useState({
    search: '',
    state: 'all', // all, active, inactive
    country: '',
    zone: '',
    minBedrooms: '',
    maxBedrooms: '',
    minPrice: '',
    maxPrice: '',
    minPhotos: '',
    maxPhotos: ''
  });

  // Tri
  const [sortConfig, setSortConfig] = useState({
    key: 'id',
    direction: 'asc'
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    filtered: 0
  });

  useEffect(() => {
    loadVillas();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [villas, filters, sortConfig]);

  useEffect(() => {
    // Calculer le nombre de pages
    setTotalPages(Math.ceil(filteredVillas.length / villasPerPage));
    setCurrentPage(1); // Reset à la page 1 quand les filtres changent
  }, [filteredVillas, villasPerPage]);

  const loadVillas = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/villas?limit=10000&apply_rules=false');
      const data = await response.json();
      
      if (data.success) {
        setVillas(data.data);
        
        // Calculer les stats
        const active = data.data.filter(v => v.state === 1).length;
        const inactive = data.data.filter(v => v.state === 0).length;
        
        setStats({
          total: data.data.length,
          active: active,
          inactive: inactive,
          filtered: data.data.length
        });
      }
    } catch (error) {
      console.error('Error loading villas:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...villas];

    // Filtre par état
    if (filters.state === 'active') {
      result = result.filter(v => v.state === 1);
    } else if (filters.state === 'inactive') {
      result = result.filter(v => v.state === 0);
    }

    // Recherche textuelle
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(v => 
        v.public_name?.toLowerCase().includes(searchLower) ||
        v.id?.toString().includes(searchLower) ||
        v.city?.toLowerCase().includes(searchLower) ||
        v.country?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre par pays
    if (filters.country) {
      result = result.filter(v => v.country === filters.country);
    }

    // Filtre par zone
    if (filters.zone) {
      result = result.filter(v => v.zone?.includes(filters.zone));
    }

    // Filtre par chambres
    if (filters.minBedrooms) {
      result = result.filter(v => v.number_bedrooms >= parseInt(filters.minBedrooms));
    }
    if (filters.maxBedrooms) {
      result = result.filter(v => v.number_bedrooms <= parseInt(filters.maxBedrooms));
    }

    // Filtre par prix
    if (filters.minPrice) {
      result = result.filter(v => (v.min_price || 0) >= parseInt(filters.minPrice));
    }
    if (filters.maxPrice) {
      result = result.filter(v => (v.max_price || 99999) <= parseInt(filters.maxPrice));
    }

    // Filtre par nombre de photos
    if (filters.minPhotos) {
      result = result.filter(v => (v.photos_count || 0) >= parseInt(filters.minPhotos));
    }
    if (filters.maxPhotos) {
      result = result.filter(v => (v.photos_count || 0) <= parseInt(filters.maxPhotos));
    }

    // Tri
    result.sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      return sortConfig.direction === 'asc' 
        ? aVal.toString().localeCompare(bVal.toString())
        : bVal.toString().localeCompare(aVal.toString());
    });

    setFilteredVillas(result);
    setStats(prev => ({ ...prev, filtered: result.length }));
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedVillas(new Set());
    } else {
      const currentPageVillas = getCurrentPageVillas();
      setSelectedVillas(new Set(currentPageVillas.map(v => v.id)));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectAllFiltered = () => {
    setSelectedVillas(new Set(filteredVillas.map(v => v.id)));
    setSelectAll(true);
  };

  const handleToggleVilla = (id) => {
    const newSelected = new Set(selectedVillas);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedVillas(newSelected);
    setSelectAll(false);
  };

  const handleSyncVilla = async (villaId) => {
    const confirmed = window.confirm(
      `Synchroniser la villa #${villaId} depuis l'API Villanovo ?\n\nCela mettra à jour toutes les données (photos, tarifs, descriptions).\n\n⚠️ Note: Si la villa est inactive dans l'API Villanovo, elle sera désactivée en BDD.`
    );

    if (!confirmed) return;

    setSyncingVilla(villaId);
    try {
      const response = await fetch(`/api/villas/${villaId}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ Villa "${data.data.name}" synchronisée avec succès !\n\n📸 ${data.data.photos} photos\n💰 ${data.data.rates} tarifs\n⏱️ ${data.data.duration}`);
        loadVillas();
      } else if (data.warning) {
        // Villa inactive dans l'API Villanovo
        alert(`⚠️ ${data.data.message}\n\nVilla: "${data.data.name}"\nStatut: ${data.data.state}\nAction: ${data.data.action === 'deactivated' ? 'Désactivée en BDD' : 'Ignorée'}`);
        loadVillas();
      } else {
        alert(`❌ Erreur: ${data.error || data.message}`);
      }
    } catch (error) {
      console.error('Sync error:', error);
      alert('❌ Erreur lors de la synchronisation');
    } finally {
      setSyncingVilla(null);
    }
  };

  const handleViewCalendar = async (villaId) => {
    setCalendarVillaId(villaId);
    setLoadingCalendar(true);
    
    try {
      // Récupérer les disponibilités de la villa
      const startDate = new Date().toISOString().split('T')[0];
      const endDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // +1 an
      
      const response = await fetch(`/api/availability/villa/${villaId}?start_date=${startDate}&end_date=${endDate}`);
      const data = await response.json();
      
      if (data.success) {
        setCalendarData(data.data);
      } else {
        alert(`❌ Erreur: ${data.error || 'Impossible de charger le calendrier'}`);
        setCalendarVillaId(null);
      }
    } catch (error) {
      console.error('Calendar error:', error);
      alert('❌ Erreur lors du chargement du calendrier');
      setCalendarVillaId(null);
    } finally {
      setLoadingCalendar(false);
    }
  };

  const handleBulkAction = async (action, customIds = null) => {
    // Utiliser customIds si fourni, sinon utiliser selectedVillas
    const idsToProcess = customIds || Array.from(selectedVillas);
    
    if (idsToProcess.length === 0) {
      alert('Veuillez sélectionner au moins une villa');
      return;
    }

    // Confirmation seulement si customIds n'est pas fourni (car déjà confirmé)
    if (!customIds) {
      const confirmed = window.confirm(
        `Êtes-vous sûr de vouloir ${action === 'activate' ? 'activer' : 'désactiver'} ${idsToProcess.length} villa(s) ?`
      );
      if (!confirmed) return;
    }

    setActionLoading(true);
    try {
      const response = await fetch('/api/villas/bulk-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids: idsToProcess,
          state: action === 'activate' ? 1 : 0
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ ${data.data.updated} villa(s) ${action === 'activate' ? 'activée(s)' : 'désactivée(s)'} avec succès`);
        setSelectedVillas(new Set());
        setSelectAll(false);
        loadVillas();
      }
    } catch (error) {
      console.error('Bulk action error:', error);
      alert('❌ Erreur lors de l\'action groupée');
    } finally {
      setActionLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      state: 'all',
      country: '',
      zone: '',
      minBedrooms: '',
      maxBedrooms: '',
      minPrice: '',
      maxPrice: '',
      minPhotos: '',
      maxPhotos: ''
    });
  };

  const getCurrentPageVillas = () => {
    const startIndex = (currentPage - 1) * villasPerPage;
    const endIndex = startIndex + villasPerPage;
    return filteredVillas.slice(startIndex, endIndex);
  };

  const exportToCSV = () => {
    const selectedData = filteredVillas.filter(v => selectedVillas.has(v.id));
    const dataToExport = selectedData.length > 0 ? selectedData : filteredVillas;

    const headers = ['ID', 'Nom', 'Pays', 'Zone', 'Ville', 'Chambres', 'Capacité', 'Prix Min', 'Prix Max', 'État'];
    const rows = dataToExport.map(v => [
      v.id,
      v.public_name,
      v.country,
      v.zone,
      v.city,
      v.number_bedrooms,
      v.number_pax,
      v.min_price,
      v.max_price,
      v.state === 1 ? 'Actif' : 'Inactif'
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `villas-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Obtenir les pays uniques
  const uniqueCountries = [...new Set(villas.map(v => v.country).filter(Boolean))].sort();
  const uniqueZones = [...new Set(villas.map(v => v.zone).filter(Boolean))].sort();

  const currentPageVillas = getCurrentPageVillas();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Chargement des villas...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-light text-gray-800" style={{ fontFamily: 'Playfair Display, serif' }}>
            Gestion des Villas
          </h2>
          <p className="text-gray-600 mt-1">Tableau complet avec filtres et actions en masse</p>
        </div>
        <Button onClick={loadVillas} variant="outline" className="border-blue-600 text-blue-700">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <p className="text-sm text-blue-700 font-medium">Total Villas</p>
          <p className="text-3xl font-bold text-blue-900">{stats.total}</p>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <p className="text-sm text-green-700 font-medium">Actives</p>
          <p className="text-3xl font-bold text-green-900">{stats.active}</p>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <p className="text-sm text-red-700 font-medium">Inactives</p>
          <p className="text-3xl font-bold text-red-900">{stats.inactive}</p>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <p className="text-sm text-amber-700 font-medium">Filtrées</p>
          <p className="text-3xl font-bold text-amber-900">{stats.filtered}</p>
        </Card>
      </div>

      {/* Filtres */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Filter className="w-5 h-5 text-blue-600" />
            Filtres
          </h3>
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Réinitialiser
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Recherche */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="ID, nom, ville, pays..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* État */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">État</label>
            <select
              value={filters.state}
              onChange={(e) => setFilters({ ...filters, state: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous</option>
              <option value="active">Actifs uniquement</option>
              <option value="inactive">Inactifs uniquement</option>
            </select>
          </div>

          {/* Pays */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pays</label>
            <select
              value={filters.country}
              onChange={(e) => setFilters({ ...filters, country: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les pays</option>
              {uniqueCountries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>

          {/* Zone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Zone</label>
            <select
              value={filters.zone}
              onChange={(e) => setFilters({ ...filters, zone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Toutes les zones</option>
              {uniqueZones.map(zone => (
                <option key={zone} value={zone}>{zone}</option>
              ))}
            </select>
          </div>

          {/* Chambres Min */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Chambres Min</label>
            <input
              type="number"
              value={filters.minBedrooms}
              onChange={(e) => setFilters({ ...filters, minBedrooms: e.target.value })}
              placeholder="Min"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Chambres Max */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Chambres Max</label>
            <input
              type="number"
              value={filters.maxBedrooms}
              onChange={(e) => setFilters({ ...filters, maxBedrooms: e.target.value })}
              placeholder="Max"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Prix Min */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Prix Min (€)</label>
            <input
              type="number"
              value={filters.minPrice}
              onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
              placeholder="Min"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Prix Max */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Prix Max (€)</label>
            <input
              type="number"
              value={filters.maxPrice}
              onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
              placeholder="Max"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Photos Min */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-blue-600" />
              Photos Min
            </label>
            <input
              type="number"
              value={filters.minPhotos}
              onChange={(e) => setFilters({ ...filters, minPhotos: e.target.value })}
              placeholder="Min"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Photos Max */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-blue-600" />
              Photos Max
            </label>
            <input
              type="number"
              value={filters.maxPhotos}
              onChange={(e) => setFilters({ ...filters, maxPhotos: e.target.value })}
              placeholder="Max"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Filtres rapides pour photos */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-3">Filtres rapides (Photos) :</p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({ ...filters, minPhotos: '', maxPhotos: '0' })}
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              Sans photos (0)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({ ...filters, minPhotos: '1', maxPhotos: '9' })}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              Peu de photos (1-9)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({ ...filters, minPhotos: '10', maxPhotos: '' })}
              className="border-green-300 text-green-700 hover:bg-green-50"
            >
              Bien fourni (10+)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({ ...filters, minPhotos: '20', maxPhotos: '' })}
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              Très complet (20+)
            </Button>
          </div>
        </div>
      </Card>

      {/* Résultats filtrés et actions rapides */}
      {(filters.search || filters.state !== 'all' || filters.country || filters.zone || 
        filters.minBedrooms || filters.maxBedrooms || filters.minPrice || filters.maxPrice ||
        filters.minPhotos || filters.maxPhotos) && filteredVillas.length > 0 && (
        <Card className="p-4 bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-900 mb-1">
                <Filter className="w-4 h-4 inline mr-2" />
                {filteredVillas.length} villa(s) correspondent aux filtres
              </p>
              <p className="text-xs text-purple-700">
                Actions rapides sur tous les résultats filtrés
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  const confirmed = window.confirm(
                    `⚠️ ATTENTION ⚠️\n\nVous êtes sur le point d'ACTIVER ${filteredVillas.length} villa(s) filtrée(s).\n\nCette action modifiera le statut de toutes les villas affichées (pas seulement celles sélectionnées).\n\nÊtes-vous sûr de vouloir continuer ?`
                  );
                  if (confirmed) {
                    handleBulkAction('activate', filteredVillas.map(v => v.id));
                  }
                }}
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700"
                size="sm"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                Activer tout ({filteredVillas.length})
              </Button>
              <Button
                onClick={() => {
                  const confirmed = window.confirm(
                    `⚠️ ATTENTION ⚠️\n\nVous êtes sur le point de DÉSACTIVER ${filteredVillas.length} villa(s) filtrée(s).\n\nCette action modifiera le statut de toutes les villas affichées (pas seulement celles sélectionnées).\n\nÊtes-vous sûr de vouloir continuer ?`
                  );
                  if (confirmed) {
                    handleBulkAction('deactivate', filteredVillas.map(v => v.id));
                  }
                }}
                disabled={actionLoading}
                className="bg-red-600 hover:bg-red-700"
                size="sm"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <EyeOff className="w-4 h-4 mr-2" />}
                Désactiver tout ({filteredVillas.length})
              </Button>
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="border-purple-600 text-purple-700"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Exporter ({filteredVillas.length})
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Actions en masse sur sélection */}
      {selectedVillas.size > 0 && (
        <Card className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-blue-900">
              {selectedVillas.size} villa(s) sélectionnée(s)
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => handleBulkAction('activate')}
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700"
                size="sm"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                Activer
              </Button>
              <Button
                onClick={() => handleBulkAction('deactivate')}
                disabled={actionLoading}
                className="bg-red-600 hover:bg-red-700"
                size="sm"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <EyeOff className="w-4 h-4 mr-2" />}
                Désactiver
              </Button>
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="border-blue-600 text-blue-700"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Exporter
              </Button>
              <Button
                onClick={handleSelectAllFiltered}
                variant="outline"
                className="border-blue-600 text-blue-700"
                size="sm"
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                Tout sélectionner ({filteredVillas.length})
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Tableau */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('id')}
                    className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900"
                  >
                    ID <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('public_name')}
                    className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900"
                  >
                    Nom <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('country')}
                    className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900"
                  >
                    Pays <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('zone')}
                    className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900"
                  >
                    Zone <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('number_bedrooms')}
                    className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900"
                  >
                    Chambres <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('number_pax')}
                    className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900"
                  >
                    Capacité <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('min_price')}
                    className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900"
                  >
                    Prix <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleSort('photos_count')}
                    className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900"
                  >
                    Photos <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('state')}
                    className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900"
                  >
                    État <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">
                  Dernière Sync Dispo
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentPageVillas.map(villa => (
                <tr
                  key={villa.id}
                  className={`hover:bg-gray-50 transition-colors ${selectedVillas.has(villa.id) ? 'bg-blue-50' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedVillas.has(villa.id)}
                      onChange={() => handleToggleVilla(villa.id)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">#{villa.id}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={villa.public_name}>
                    {villa.public_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{villa.country}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{villa.zone}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 text-center">{villa.number_bedrooms}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 text-center">{villa.number_pax}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {villa.min_price ? `${villa.min_price.toLocaleString('fr-FR')}€` : '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge
                      variant="outline"
                      className={
                        villa.photos_count === 0 
                          ? 'bg-red-50 text-red-700 border-red-200' 
                          : villa.photos_count < 10 
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-green-50 text-green-700 border-green-200'
                      }
                    >
                      <ImageIcon className="w-3 h-3 mr-1" />
                      {villa.photos_count || 0}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant={villa.state === 1 ? 'default' : 'secondary'}
                      className={villa.state === 1 ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}
                    >
                      {villa.state === 1 ? (
                        <>
                          <Check className="w-3 h-3 mr-1" />
                          Actif
                        </>
                      ) : (
                        <>
                          <X className="w-3 h-3 mr-1" />
                          Inactif
                        </>
                      )}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    {villa.last_availability_sync ? (
                      <div className="text-xs text-gray-600">
                        {new Date(villa.last_availability_sync).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Jamais</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleSyncVilla(villa.id)}
                        disabled={syncingVilla === villa.id}
                        variant="outline"
                        size="sm"
                        className="border-blue-600 text-blue-700 hover:bg-blue-50"
                        title="Synchroniser les données"
                      >
                        {syncingVilla === villa.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3 h-3" />
                        )}
                      </Button>
                      <Button
                        onClick={() => handleViewCalendar(villa.id)}
                        variant="outline"
                        size="sm"
                        className="border-green-600 text-green-700 hover:bg-green-50"
                        title="Voir le calendrier et réservations"
                      >
                        <Calendar className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Affichage de {((currentPage - 1) * villasPerPage) + 1} à {Math.min(currentPage * villasPerPage, filteredVillas.length)} sur {filteredVillas.length} villa(s)
          </p>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-700">
              Page {currentPage} sur {totalPages}
            </span>
            <Button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              variant="outline"
              size="sm"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Modal Calendrier */}
      {calendarVillaId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-blue-600" />
                  Calendrier & Réservations - Villa #{calendarVillaId}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {calendarData?.villa_name || 'Chargement...'}
                </p>
              </div>
              <button
                onClick={() => {
                  setCalendarVillaId(null);
                  setCalendarData(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingCalendar ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : calendarData ? (
                <div className="space-y-6">
                  {/* Statistiques */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-600 font-medium">Jours Réservés</p>
                      <p className="text-3xl font-bold text-red-700 mt-2">
                        {calendarData.availability?.filter(d => d.status === 'booked').length || 0}
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-green-600 font-medium">Jours Disponibles</p>
                      <p className="text-3xl font-bold text-green-700 mt-2">
                        {calendarData.availability?.filter(d => d.status === 'available').length || 0}
                      </p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-600 font-medium">Dernière Mise à Jour</p>
                      <p className="text-sm font-bold text-blue-700 mt-2">
                        {calendarData.last_updated 
                          ? new Date(calendarData.last_updated).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Liste des réservations */}
                  {calendarData.availability && calendarData.availability.length > 0 ? (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Réservations ({calendarData.availability.filter(d => d.status === 'booked').length})
                      </h3>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {calendarData.availability
                          .filter(d => d.status === 'booked')
                          .map((booking, index) => (
                            <div
                              key={index}
                              className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between"
                            >
                              <div className="flex items-center gap-3">
                                <Badge className="bg-red-100 text-red-800 border-red-200">
                                  🔴 Réservé
                                </Badge>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {new Date(booking.date).toLocaleDateString('fr-FR', {
                                      weekday: 'short',
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </p>
                                  {booking.booking_ref && (
                                    <p className="text-xs text-gray-500">Réf: {booking.booking_ref}</p>
                                  )}
                                </div>
                              </div>
                              {booking.min_stay && (
                                <Badge variant="outline" className="text-xs">
                                  Min {booking.min_stay} nuits
                                </Badge>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Aucune donnée de disponibilité pour cette villa</p>
                      <p className="text-sm mt-2">Lancez une synchronisation pour récupérer le calendrier</p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <Button
                onClick={() => {
                  setCalendarVillaId(null);
                  setCalendarData(null);
                }}
                variant="outline"
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVillas;

